import { CSSProperties, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { ResetIcon } from "@/assets/icons";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  DEFAULT_SIMPLE_FILL_COLOR,
  DEFAULT_SIMPLE_OUTLINE_COLOR
} from "@/constants/dataset";
import { cn } from "@/lib/utils";
import {
  toggleAreaAdminPopup,
  toggleCatchmentPopup,
  toggleDataWeightSettingsPopup,
  toggleGlobalFilterPopup
} from "@/store/slice/uiSlice";
import type {
  ColorTarget,
  RowMarkerFields,
  SimpleFillValue
} from "@/types/dataset";

import { Icon } from "equator-design-system";

import ColorPickerPanel from "./ColorPickerPanel";
import { LegendColumnHeaders, TRANSPARENT_CHECKERBOARD } from "./LegendRow";
import MarkerVisualPanel from "./MarkerVisualPanel";

interface SimpleFillConfigProps {
  attributeLabel?: string;
  isPOI?: boolean;
  /** Line datasets: single "Stroke" color column (no Fill/outline), drives line-color. */
  strokeOnly?: boolean;
  disabled?: boolean;
  value?: SimpleFillValue;
  onPreview?: (value: SimpleFillValue) => void;
  onSave?: (value: SimpleFillValue) => void;
  onCancel?: () => void;
  onReset?: () => SimpleFillValue | null;
}

const getDefaultValue = (label: string): SimpleFillValue => ({
  fillColor: DEFAULT_SIMPLE_FILL_COLOR,
  outlineColor: DEFAULT_SIMPLE_OUTLINE_COLOR,
  label,
  imageUrl: null,
  imageName: null,
  imageAssetId: null,
  iconId: null
});

const SimpleFillConfig = ({
  attributeLabel = "Category",
  isPOI = false,
  strokeOnly = false,
  disabled = false,
  value,
  onPreview,
  onSave,
  onCancel,
  onReset
}: SimpleFillConfigProps) => {
  const dispatch = useDispatch();

  const committed = value ?? getDefaultValue(attributeLabel);
  const [draft, setDraft] = useState<SimpleFillValue>(committed);
  const [useImage, setUseImage] = useState<boolean>(
    () => !!committed.imageUrl || !!committed.iconId
  );
  const [activePicker, setActivePicker] = useState<ColorTarget | null>(null);
  const lastMarkerRef = useRef<RowMarkerFields | null>(null);

  const latestSaved = JSON.stringify(committed);
  const [syncedKey, setSyncedKey] = useState(latestSaved);
  if (latestSaved !== syncedKey) {
    if (JSON.stringify(draft) === syncedKey) {
      setDraft(committed);
      setUseImage(!!committed.imageUrl || !!committed.iconId);
    }
    setSyncedKey(latestSaved);
  }
  const isDirty = JSON.stringify(draft) !== latestSaved;

  const updateDraft = (patch: Partial<SimpleFillValue>) => {
    const next = { ...draft, ...patch };
    setDraft(next);
    onPreview?.(next);
  };

  const openPicker = (target: ColorTarget["target"]) => {
    if (disabled) return;
    setActivePicker({ target });
    dispatch(toggleDataWeightSettingsPopup(false));
    dispatch(toggleCatchmentPopup(false));
    dispatch(toggleAreaAdminPopup(false));
    dispatch(toggleGlobalFilterPopup(false));
  };

  // Change to image or icon mode
  const handleToggleImage = (checked: boolean) => {
    setUseImage(checked);
    if (checked) {
      const marker = lastMarkerRef.current;
      if (marker) {
        updateDraft({
          imageUrl: marker.image,
          imageName: marker.imageName,
          imageAssetId: marker.imageAssetId,
          iconId: marker.iconId
        });
      }
      openPicker("image");
    } else {
      if (draft.imageUrl || draft.iconId) {
        lastMarkerRef.current = {
          image: draft.imageUrl,
          imageName: draft.imageName,
          imageAssetId: draft.imageAssetId,
          iconId: draft.iconId
        };
      }
      setActivePicker(null);
      updateDraft({
        imageUrl: null,
        imageName: null,
        imageAssetId: null,
        iconId: null
      });
    }
  };

  const imageMode = useImage && isPOI;

  // Resetting config to default
  const handleReset = () => {
    const resetConfig = onReset?.();
    const next = resetConfig ?? getDefaultValue(attributeLabel);
    setDraft(next);
    if (!resetConfig) onPreview?.(next);
    setUseImage(false);
    setActivePicker(null);
    lastMarkerRef.current = null;
  };

  // Saving config
  const handleSave = () => {
    onSave?.(draft);
    setActivePicker(null);
  };

  // Canceling edits config
  const handleCancel = () => {
    setDraft(committed);
    onCancel?.();
    setActivePicker(null);
  };

  // Fill swatch: the flat color — or, in image mode, the uploaded image, the
  // selected icon (rendered as a child), or the checkerboard placeholder.
  let fillSwatchStyle: CSSProperties = { backgroundColor: draft.fillColor };
  if (imageMode) {
    if (draft.iconId) {
      fillSwatchStyle = {};
    } else {
      fillSwatchStyle = draft.imageUrl
        ? {
            backgroundImage: `url(${draft.imageUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center"
          }
        : { background: TRANSPARENT_CHECKERBOARD };
    }
  }

  return (
    <div className="flex flex-col gap-2 rounded-xl border border-[#e5e7eb] p-3">
      {/* Image toggle + Reset */}
      <div className="flex items-center justify-between">
        {isPOI ? (
          <label className="flex items-center gap-2 text-xs text-txtHeading">
            <Switch
              checked={useImage}
              onCheckedChange={handleToggleImage}
              disabled={disabled}
              data-testid="simple-image-toggle"
            />
            Image
          </label>
        ) : (
          <span />
        )}
        <button
          type="button"
          onClick={handleReset}
          disabled={disabled}
          className="text-xs text-primary-teal disabled:opacity-50"
          data-testid="simple-reset"
        >
          <div className="flex flex-row items-center">
            <ResetIcon className="mr-1 inline h-3 w-3" />
            Reset
          </div>
        </button>
      </div>

      <LegendColumnHeaders imageMode={imageMode} strokeOnly={strokeOnly} />

      {/* Single Simple row */}
      <div
        className={cn(
          "grid items-center gap-2",
          imageMode || strokeOnly
            ? "grid-cols-[auto_1fr]"
            : "grid-cols-[auto_auto_1fr]"
        )}
      >
        {/* Fill is always clickable. In image mode it shows the applied marker
            (image/icon, or the checkerboard) and opens the Marker Visual
            panel; otherwise it opens the color picker. */}
        <button
          type="button"
          onClick={() => openPicker(imageMode ? "image" : "fill")}
          disabled={disabled}
          aria-label={strokeOnly ? "Stroke color" : "Fill color"}
          data-testid="simple-fill-swatch"
          className={cn(
            "flex size-9 shrink-0 items-center justify-center rounded-md border border-gainsboro disabled:opacity-50",
            (activePicker?.target === "fill" ||
              activePicker?.target === "image") &&
              "ring-2 ring-yellow-400 ring-offset-0"
          )}
          style={fillSwatchStyle}
        >
          {imageMode && draft.iconId && (
            <Icon
              name={draft.iconId}
              width={20}
              height={20}
              className="text-txtHeading"
            />
          )}
        </button>
        {!imageMode && !strokeOnly && (
          <button
            type="button"
            onClick={() => openPicker("outline")}
            disabled={disabled}
            aria-label="Outline color"
            data-testid="simple-outline-swatch"
            className={cn(
              "size-9 shrink-0 rounded-md border border-gainsboro disabled:opacity-50",
              activePicker?.target === "outline" &&
                "ring-2 ring-yellow-400 ring-offset-0"
            )}
            style={{ backgroundColor: draft.outlineColor }}
          />
        )}
        <input
          type="text"
          value={draft.label}
          onChange={(e) => updateDraft({ label: e.currentTarget.value })}
          disabled={disabled}
          aria-label="Category label"
          data-testid="simple-category-input"
          className="h-9 w-full rounded-lg border border-gainsboro px-3 text-xs text-txtHeading outline-none focus:border-primary-teal disabled:opacity-50"
        />
      </div>

      {/* Cancel / Save */}
      <div className="flex items-center gap-2 pt-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleCancel}
          disabled={disabled || !isDirty}
          className="h-7 flex-1 rounded-lg text-xs"
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={handleSave}
          disabled={disabled || !isDirty}
          className="h-7 flex-1 rounded-lg text-xs"
        >
          Save
        </Button>
      </div>

      {activePicker?.target === "image" ? (
        <MarkerVisualPanel
          initialImage={
            draft.imageUrl
              ? {
                  url: draft.imageUrl,
                  name: draft.imageName ?? "image",
                  assetId: draft.imageAssetId
                }
              : null
          }
          initialIcon={draft.iconId}
          onApply={(image) =>
            // Icon and image are mutually exclusive (ORB-3394): applying one
            // clears the other.
            updateDraft({
              imageUrl: image.url,
              imageName: image.name,
              imageAssetId: image.assetId,
              iconId: null
            })
          }
          onApplyIcon={(iconId) =>
            updateDraft({
              iconId,
              imageUrl: null,
              imageName: null,
              imageAssetId: null
            })
          }
          onRemove={() =>
            updateDraft({
              imageUrl: null,
              imageName: null,
              imageAssetId: null,
              iconId: null
            })
          }
          onClose={() => setActivePicker(null)}
        />
      ) : (
        activePicker && (
          <ColorPickerPanel
            // Remount per target so color state resets to what was clicked.
            key={activePicker.target}
            mode="color"
            color={
              activePicker.target === "outline"
                ? draft.outlineColor
                : draft.fillColor
            }
            onColorChange={(hex) => {
              // Actually changing a color (not just opening the picker)
              // switches off image mode.
              setUseImage(false);
              updateDraft(
                activePicker.target === "outline"
                  ? { outlineColor: hex }
                  : { fillColor: hex }
              );
            }}
            onClose={() => setActivePicker(null)}
          />
        )
      )}
    </div>
  );
};

export default SimpleFillConfig;
